import { useEffect } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { ensureSignupConversionFired } from '@/lib/signupConversion';

// Session-scoped guard so we don't re-hit the DB on every route change.
// The atomic DB claim in ensureSignupConversionFired is the real
// cross-session / cross-tab dedupe; this just avoids redundant calls.
const attemptedUserIds = new Set<string>();

/**
 * Fires the sign_up conversion exactly once per user, decoupled from the
 * /welcome page and the fragile signup-confirming sessionStorage marker.
 *
 * Pass the authenticated user ONLY when the app has reached the point where
 * sign_up should count (email confirmed + MFA satisfied — the same stage the
 * old /welcome placement implied, so cookie-consent timing is unchanged).
 * Pass null otherwise; the hook no-ops until a non-null user arrives.
 */
export function useSignupConversion(user: User | null): void {
  const userId = user?.id;
  const email = user?.email ?? undefined;

  useEffect(() => {
    if (!userId) return;
    if (attemptedUserIds.has(userId)) return;
    attemptedUserIds.add(userId);
    void ensureSignupConversionFired(supabase, userId, email);
  }, [userId, email]);
}
